import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { entityPath, pagePath } from "../config/pages";
import { getLocation } from "../data/locations";
import { TREATMENTS } from "../data/treatments";
import { PageHero, CTABand, SectionHeading } from "../components/Sections";
import { Reveal, RevealStagger, RevealItem } from "../components/motion/Reveal";
import { TreatmentIcon } from "../components/iconMap";
import ConsultationForm from "../components/ConsultationForm";
import { webPageSchema, breadcrumbSchema } from "../lib/schema";

export default function LocationPage({ locationKey }) {
  const { t, lang, p, e } = useI18n();
  const lo = getLocation(locationKey);
  const alternates = {
    en: entityPath(null, lo.slug, "en"),
    fr: entityPath(null, lo.slug, "fr"),
    de: entityPath(null, lo.slug, "de"),
  };
  useRegisterAlternates(alternates);

  const name = lo.name[lang] || lo.name.en;
  const path = entityPath(null, lo.slug, lang);
  const crumbs = [
    { name: t.nav.locations, to: p("dentalTourism") },
    { name, to: path },
  ];

  return (
    <>
      <Seo
        title={lo.metaTitle[lang] || lo.metaTitle.en}
        description={lo.metaDesc[lang] || lo.metaDesc.en}
        path={path}
        alternates={alternates}
        lang={lang}
        image={lo.image}
        jsonLd={[
          webPageSchema({ name, description: lo.metaDesc[lang] || lo.metaDesc.en, path }),
          breadcrumbSchema([{ name: t.common.home, path: p("home") }, ...crumbs.map((c) => ({ name: c.name, path: c.to }))]),
        ]}
      />

      <PageHero
        eyebrow={t.locationsSection.eyebrow}
        title={name}
        intro={lo.tagline[lang] || lo.tagline.en}
        crumbs={crumbs}
        image={{ src: lo.image, alt: `${name}, Vietnam` }}
      />

      <div className="container-dv py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="prose-dv">
                {(lo.intro[lang] || lo.intro.en).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {(lo.highlights[lang] || lo.highlights.en).map((h, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-2xl border border-navy/8 bg-white px-5 py-4 shadow-soft">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-turquoise/10 text-turquoise">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-[15px] font-semibold text-navy/80">{h}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Rich unique city sections (optional) */}
            {lo.sections && lo.sections.length > 0 && (
              <div className="mt-10 space-y-8">
                {lo.sections.map((s, i) => (
                  <Reveal key={i} delay={0.03 * i}>
                    <section>
                      <h2 className="text-2xl sm:text-3xl text-navy">{s.h[lang] || s.h.en}</h2>
                      <div className="prose-dv mt-3">
                        {(s.body[lang] || s.body.en).map((para, j) => <p key={j}>{para}</p>)}
                      </div>
                    </section>
                  </Reveal>
                ))}
              </div>
            )}

            {/* Treatments available */}
            <Reveal delay={0.05}>
              <h2 className="mt-12 text-2xl sm:text-3xl text-navy">{t.treatmentsSection.title}</h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {TREATMENTS.slice(0, 8).map((tr) => (
                  <Link
                    key={tr.key}
                    to={e(tr.slug)}
                    data-testid={`location-treatment-${tr.key}`}
                    className="group flex items-center gap-3 rounded-2xl border border-navy/8 bg-white px-4 py-3.5 shadow-soft transition-all hover:shadow-lift hover:-translate-y-0.5"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dental/8 text-dental">
                      <TreatmentIcon name={tr.icon} className="h-5 w-5" />
                    </span>
                    <span className="flex-1 text-[15px] font-semibold text-navy">{tr.name[lang] || tr.name.en}</span>
                    <ArrowRight className="h-4 w-4 text-navy/30 transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </Reveal>

            <div className="mt-10 rounded-2xl border border-navy/10 bg-mist p-6 text-sm leading-relaxed text-navy/70">
              <strong className="text-navy">{t.common.disclaimerTitle}.</strong> {t.common.disclaimer}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <h2 className="mb-4 text-xl text-navy">{t.form.title}</h2>
              <ConsultationForm defaultCity={lo.key} />
            </div>
          </aside>
        </div>
      </div>

      <CTABand />
    </>
  );
}
